<template>
  <div v-if="auth.user && recommendations.length > 0" class="recommendations">
    <div class="recommendations__header">
      <p>{{ $t("recommendations") }}</p>
    </div>

    <swiper
      :modules="modules"
      :space-between="10"
      :free-mode="true"
      :free-mode-sticky="true"
      :slides-per-view="'auto'"
      class="recommendations__list"
    >
      <template v-if="isLoading">
        <swiper-slide
          v-for="n in 5"
          :key="'loader-' + n"
          class="recommendations__item"
        >
          <RecommendetLoader />
        </swiper-slide>
      </template>

      <template v-else>
        <swiper-slide
          v-for="(user, index) in recommendations"
          :key="index"
          class="recommendations__item"
          @click="openProfile(user.id)"
        >
          <div class="recommendations__item_content">
            <!-- <button class="recommendations__close" @click.stop="blockUserFromRecommendations(user.id)"></button> -->
            <div
              class="recommendations__bg"
              :style="{ backgroundImage: `url(${recommendationBgUrl(user)})` }"
            ></div>

            <Avatar :user="user" class="recommendations__avatar" />
            <p>{{ user.name || "@" + user.username }}</p>

            <FollowBtn
              :userId="user.id"
              :isFollowing="user.i_follow_him"
              @click.stop
              class="recommendations__follow"
            />
          </div>
        </swiper-slide>
      </template>
    </swiper>
  </div>
</template>

<script>
import { GetRecommendations } from "@/api/axios";
import { useAuthStore } from "@/stores/authStore";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/free-mode";
import { FreeMode } from "swiper/modules";
import FollowBtn from "@/UI/Button/FollowBtn.vue";
import RecommendetLoader from "@/UI/Loaders/RecommendetLoader.vue";
import { blockUserRecommendations } from "@/api/axios";
import Avatar from "@/components/Avatar.vue";
import { useThemeStore } from "@/stores/themeStore";
import { useOpenProfile } from "@/composables/useOpenProfile.js";

export default {
  name: "Recommendations",
  components: {
    Swiper,
    SwiperSlide,
    FollowBtn,
    RecommendetLoader,
    Avatar,
  },
  emits: ["_freeModeStaticRelease"],
  setup() {
    return {
      modules: [FreeMode],
    };
  },
  data() {
    return {
      recommendations: [],
      auth: useAuthStore(),
      isLoading: false,
      openProfile: useOpenProfile().openProfile,
    };
  },
  methods: {
    async loadRecommendations() {
      if (this.loading) return;
      this.isLoading = true;

      if (this.auth.user) {
        try {
          const res = await GetRecommendations();
          const recommendations = res.data;

          this.recommendations = recommendations;
        } catch (e) {
          console.error("Failed to load recommendations:", e);
        } finally {
          this.isLoading = false;
        }
      }
    },
    defaultAvatar() {
      const themeStore = useThemeStore();

      return themeStore.isDark
        ? "/default-avatar-dark.svg"
        : "/default-avatar-lite.svg";
    },
    recommendationBgUrl(user) {
      const userpic = user?.userpic;
      if (!userpic || !userpic.url) {
        return this.defaultAvatar();
      }
      return import.meta.env.VITE_IMG_BASE_URL + userpic.related;
    },
    async blockUserFromRecommendations(userId) {
      try {
        const data = { is_show_recommended: false };

        await blockUserRecommendations(userId, data);
        this.recommendations = this.recommendations.filter(
          (u) => u.id !== userId,
        );
      } catch (e) {
        console.error("Ошибка при блокировке пользователя в рекомендациях:", e);
      }
    },
  },
  watch: {
    "auth.user"(newUser) {
      if (newUser) {
        this.loadRecommendations();
      }
    },
  },
  mounted() {
    if (this.auth.user) {
      this.loadRecommendations();
    }
  },
};
</script>

<style>
.recommendations {
  background-color: var(--header-color);
  border-radius: 25px;
  padding-bottom: 24px;
  margin-bottom: 12px;
}

.recommendations__header {
  padding: 12px 12px 0 24px;
}

.recommendations__close {
  background: rgba(0, 0, 0, 0.25);
  border: none;
  cursor: pointer;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  position: absolute;
  right: 5px;
  top: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.recommendations__close::before,
.recommendations__close::after {
  content: "";
  position: absolute;
  width: 10px;
  height: 2px;
  background-color: white;
  border-radius: 2px;
}

.recommendations__close::before {
  transform: rotate(45deg);
}

.recommendations__close::after {
  transform: rotate(-45deg);
}

.recommendations__close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.recommendations__list {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding-top: 12px;
}

.recommendations__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(10px);
  transform: scale(1.5);
  z-index: -1;
}

.recommendations__bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--recommendation-blur);
  z-index: 1;
}

.recommendations__item {
  border-radius: 15px;
  width: 137px;
  background-color: var(--recommendation-bg);
  border: 1px solid var(--recommendation-border);
  cursor: pointer;
  z-index: 3;
  position: relative;
  height: auto;
  transition: transform 0.2s ease;
}

.recommendations__item_content {
  padding: 8px 7px;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
}

.recommendations__item:hover {
  transform: translateY(-5px);
}

.recommendations__item p {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.recommendations__item:first-child {
  margin-left: 24px;
}

.recommendations__item:last-child {
  margin-right: 24px;
}

.recommendations__avatar {
  width: 90px !important;
  height: 90px !important;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 8px;
}

.recommendations__follow {
  border-radius: 10px;
  background: var(--follow-bg) !important;
  border: 1px solid var(--border-follow);
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
  color: var(--text-color);
  cursor: pointer;
  transition:
    background 0.2s ease,
    border 0.2s ease;
}

.recommendations__follow:hover {
  background: var(--follow-hover) !important;
  border: 1px solid var(--hover);
}
</style>
